// src/secure/users/UserCreate.tsx
import React, { Component, SyntheticEvent } from "react";
import { Role } from "../../classes/role";
import Wrapper from "../Wrapper";
import axios from "axios";
import { Redirect } from "react-router-dom";

type Country = { id: number; name: string };

type State = {
    roles: Role[];
    countries: Country[];
    redirect: boolean;
    country_name: string;
    loading: boolean;
    error?: string;
};

class UserCreate extends Component<{}, State> {
    state: State = {
        roles: [],
        countries: [],
        redirect: false,
        country_name: "",
        loading: true,
        error: undefined,
    };

    first_name = "";
    last_name = "";
    email = "";
    role_id = 0;
    // keep a class field in case you want to track independently of state
    country_name = "";

    componentDidMount = async () => {
        try {
            // Load roles, current user (for default country), and countries list
            const [rolesRes, userRes, countriesRes] = await Promise.all([
                axios.get("roles"),
                axios.get("user/"),
                axios.get("selections/country/"),
            ]);

            const currentCountry: string = userRes?.data?.data?.country || "";

            // API returns: data: [ { "1": "Burundi" }, { "2": "Kenya" }, ... ]
            const rawCountries: Array<Record<string, string>> =
                countriesRes?.data?.data || [];

            const countries: Country[] = rawCountries.flatMap((obj) =>
                Object.entries(obj).map(([id, name]) => ({
                    id: parseInt(id, 10),
                    name,
                }))
            );

            this.country_name = currentCountry; // sync class field
            this.setState({
                roles: rolesRes?.data?.data || [],
                countries,
                country_name: currentCountry,
                loading: false,
            });
        } catch (err: any) {
            this.setState({
                loading: false,
                error:
                    err?.response?.data?.message ||
                    "Failed to load form data. Please try again.",
            });
        }
    };

    submit = async (e: SyntheticEvent) => {
        e.preventDefault();

        const payload = {
            first_name: this.first_name,
            last_name: this.last_name,
            email: this.email,
            role_id: this.role_id,
            // Prefer state (controlled select); fallback to class field
            country_name: this.state.country_name || this.country_name,
        };

        try {
            await axios.post("users/?type=create", payload);
            this.setState({ redirect: true });
        } catch (err: any) {
            this.setState({
                error:
                    err?.response?.data?.message ||
                    "Failed to create user. Please review the fields and try again.",
            });
        }
    };

    render() {
        if (this.state.redirect) {
            return <Redirect to={"/users"} />;
        }

        return (
            <Wrapper>
                <form onSubmit={this.submit}>
                    <h3 className="mb-3">Create User</h3>

                    {this.state.error && (
                        <div className="alert alert-danger" role="alert">
                            {this.state.error}
                        </div>
                    )}

                    <div className="form-group">
                        <label>First Name</label>
                        <input
                            type="text"
                            className="form-control"
                            name="first_name"
                            onChange={(e) => (this.first_name = e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Last Name</label>
                        <input
                            type="text"
                            className="form-control"
                            name="last_name"
                            onChange={(e) => (this.last_name = e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            className="form-control"
                            name="email"
                            onChange={(e) => (this.email = e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Country</label>
                        <select
                            className="form-control"
                            name="country_name"
                            value={this.state.country_name}
                            onChange={(e) => {
                                this.country_name = e.target.value; // keep class field in sync
                                this.setState({ country_name: e.target.value });
                            }}
                            required
                            disabled={this.state.loading}
                        >
                            <option value="" disabled>
                                {this.state.loading ? "Loading countries..." : "Select Country"}
                            </option>
                            {this.state.countries.map((c) => (
                                <option key={c.id} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Role</label>
                        <select
                            name="role_id"
                            className="form-control"
                            onChange={(e) => (this.role_id = parseInt(e.target.value))}
                            required
                            disabled={this.state.loading}
                        >
                            <option value="">
                                {this.state.loading ? "Loading roles..." : "Select Role"}
                            </option>
                            {this.state.roles.map((role: Role) => (
                                <option key={role.id} value={role.id}>
                                    {role.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        className="btn btn-outline-secondary"
                        type="submit"
                        disabled={this.state.loading}
                    >
                        Save
                    </button>
                </form>
            </Wrapper>
        );
    }
}

export default UserCreate;
